import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiRequest } from '@/lib/queryClient';
import { toast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CheckCircle, Mail, Loader2, FileText, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

// Email setting form schema
const emailSettingsSchema = z.object({
  provider: z.enum(['sendgrid', 'mailgun'], {
    required_error: 'Please select an email provider',
  }),
  fromEmail: z.string().email({
    message: 'Please enter a valid email address',
  }),
  fromName: z.string().min(1, {
    message: 'Please enter a sender name',
  }),
  apiKey: z.string().optional(),
  domain: z.string().optional(),
  region: z.string().optional(),
});

type EmailSettings = z.infer<typeof emailSettingsSchema>;

// Email test form schema
const emailTestSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address',
  }),
});

export default function EmailSettingsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [, setLocation] = useLocation();
  const [testSuccess, setTestSuccess] = useState<boolean | null>(null);
  const [testMessage, setTestMessage] = useState('');
  
  const queryClient = useQueryClient();
  
  // Fetch current email settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['/api/email-settings'],
    refetchOnWindowFocus: false,
  });
  
  // Fetch email statistics
  const { data: stats } = useQuery({
    queryKey: ['/api/email-stats'],
    refetchOnWindowFocus: false,
  });
  
  // Initialize the form with default values
  const form = useForm<EmailSettings>({
    resolver: zodResolver(emailSettingsSchema),
    defaultValues: {
      provider: 'mailgun',
      fromEmail: 'noreply@mailauth.saasgama.com',
      fromName: 'SaaSGama',
      apiKey: '',
      domain: 'mailauth.saasgama.com',
      region: '',
    },
  });
  
  // Email test form
  const testForm = useForm({
    resolver: zodResolver(emailTestSchema),
    defaultValues: {
      email: '',
    },
  });
  
  // Update form values when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        provider: settings.provider || 'mailgun',
        fromEmail: settings.fromEmail || 'noreply@mailauth.saasgama.com',
        fromName: settings.fromName || 'SaaSGama',
        apiKey: '', // Never pre-fill the API key for security
        domain: settings.domain || 'mailauth.saasgama.com',
        region: settings.region || '',
      });
    }
  }, [settings, form]);
  
  // Mutation for updating email settings
  const mutation = useMutation({
    mutationFn: async (data: EmailSettings) => {
      console.log('Sending email settings:', data);
      return apiRequest('/api/email-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Email settings saved successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/email-settings'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-stats'] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to save email settings',
        variant: 'destructive',
      });
    },
  });
  
  // Mutation for sending test email
  const testEmailMutation = useMutation({
    mutationFn: async (data: { email: string }) => {
      return apiRequest('/api/email/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: data,
      });
    },
    onSuccess: (data) => {
      setTestSuccess(data.success);
      setTestMessage(data.message);
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Test email sent successfully',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send test email',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      setTestSuccess(false);
      setTestMessage('Failed to send test email');
      toast({
        title: 'Error',
        description: 'Failed to send test email',
        variant: 'destructive',
      });
    },
  });
  
  // Form submission handler
  const onSubmit = (data: EmailSettings) => {
    mutation.mutate(data);
  };
  
  // Test email submission handler
  const onTestSubmit = (data: { email: string }) => {
    setTestSuccess(null);
    setTestMessage('');
    testEmailMutation.mutate(data);
  };
  
  // Helper to determine if API key is required based on provider and current configuration
  const isApiKeyRequired = (provider: string) => {
    // If changing provider or not configured, require API key
    return !settings?.isConfigured || provider !== settings?.provider;
  };
  
  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Email Configuration</h1>
        <p className="text-gray-500 mt-2">
          Configure email providers, campaigns, and templates for system notifications and communication
        </p>
      </div>
      
      <Tabs 
        value={activeTab} 
        onValueChange={(val) => {
          if (val === 'templates') {
            setLocation('/admin/email/templates');
          } else {
            setActiveTab(val);
          }
        }}
      >
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Providers</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="test">Test Email</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Email Service Overview</CardTitle>
              <CardDescription>Status of your email service and recent activity</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : !settings?.isConfigured ? (
                <div className="space-y-6">
                  <Alert className="bg-amber-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Email not configured</AlertTitle>
                    <AlertDescription>
                      Configure an email provider to enable system notifications and communications
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex justify-center">
                    <Button onClick={() => setActiveTab('settings')}>
                      Configure Email Provider
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="bg-green-50 p-3 rounded-full">
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium">Email Service Active</p>
                      <p className="text-sm text-muted-foreground">
                        Using {settings.provider} as your email provider
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Email Provider</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold capitalize">{settings.provider}</div>
                        <p className="text-sm text-muted-foreground">
                          {settings.fromName} &lt;{settings.fromEmail}&gt;
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Templates</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">0</div>
                        <p className="text-sm text-muted-foreground">
                          No templates created yet
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setActiveTab('settings')}>
                      Configure Provider
                    </Button>
                    <Button onClick={() => setActiveTab('test')}>
                      Test Configuration
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Email Provider Configuration</CardTitle>
              <CardDescription>
                Set up your preferred email provider for sending notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-2/3" />
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="provider"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Provider</FormLabel>
                          <Select 
                            onValueChange={(value) => {
                              field.onChange(value);
                              // Reset API key when provider changes
                              form.setValue('apiKey', '');
                            }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a provider" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="mailgun">Mailgun</SelectItem>
                              <SelectItem value="sendgrid">SendGrid</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Choose your email service provider
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="fromEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>From Email</FormLabel>
                            <FormControl>
                              <Input placeholder="notifications@example.com" {...field} />
                            </FormControl>
                            <FormDescription>
                              Email address that will appear as the sender
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="fromName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>From Name</FormLabel>
                            <FormControl>
                              <Input placeholder="SaaSGama" {...field} />
                            </FormControl>
                            <FormDescription>
                              Name that will appear as the sender
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="apiKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>API Key {isApiKeyRequired(form.getValues().provider) ? '(Required)' : '(Optional)'}</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder={settings?.isConfigured ? '••••••••••••••••••••••••••' : 'Enter API key'} 
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            {settings?.isConfigured 
                              ? 'Leave empty to keep using the existing API key' 
                              : 'API key for authentication with the provider'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {form.watch('provider') === 'mailgun' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="domain"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Domain</FormLabel>
                              <FormControl>
                                <Input placeholder="mail.example.com" {...field} />
                              </FormControl>
                              <FormDescription>
                                Mailgun verified domain
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="region"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Region</FormLabel>
                              <Select 
                                onValueChange={field.onChange}
                                defaultValue={field.value || 'us'}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select region" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="us">US</SelectItem>
                                  <SelectItem value="eu">EU</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormDescription>
                                Mailgun server region
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                    
                    {settings?.isConfigured && (
                      <Alert variant="success" className="bg-green-50 mb-6">
                        <CheckCircle className="h-4 w-4" />
                        <AlertTitle>Provider Configured</AlertTitle>
                        <AlertDescription>
                          {settings.provider} is currently configured and ready to send emails.
                          {settings.lastTestedAt && (
                            <div className="text-sm mt-1">
                              Last tested: {new Date(settings.lastTestedAt).toLocaleString()}
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        disabled={mutation.isPending}
                      >
                        {mutation.isPending ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        
        <TabsContent value="templates">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Email Templates</CardTitle>
                <CardDescription>Manage reusable email templates</CardDescription>
              </div>
              <Button className="ml-auto" onClick={() => window.location.href = '/admin/email/templates'}>
                <FileText className="mr-2 h-4 w-4" /> Manage Templates
              </Button>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Templates Available</AlertTitle>
                <AlertDescription>
                  Create and manage reusable email templates for your communications. Templates can be used for notification emails, marketing messages, and transactional emails.
                </AlertDescription>
              </Alert>
              
              <div className="mt-6 flex flex-col items-center justify-center p-6 text-center bg-gray-50 rounded-lg border border-dashed">
                <FileText className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium mb-2">Create Your First Template</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Templates make it easy to maintain consistent branding and messaging across all your email communications.
                </p>
                <Button onClick={() => window.location.href = '/admin/email/templates'}>
                  Go to Templates
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle>Test Email Configuration</CardTitle>
              <CardDescription>
                Send a test email to verify your configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!settings?.isConfigured && !stats?.provider && (
                <Alert variant="warning" className="bg-amber-50 mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Provider Not Configured</AlertTitle>
                  <AlertDescription>
                    You need to configure an email provider in the Settings tab first.
                  </AlertDescription>
                </Alert>
              )}
              
              <Form {...testForm}>
                <form onSubmit={testForm.handleSubmit(onTestSubmit)} className="space-y-6">
                  <FormField
                    control={testForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recipient Email</FormLabel>
                        <FormControl>
                          <Input placeholder="your@email.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Where to send the test email
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {testSuccess !== null && (
                    <Alert 
                      variant={testSuccess ? 'success' : 'destructive'} 
                      className={testSuccess ? 'bg-green-50' : 'bg-red-50'}
                    >
                      {testSuccess ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      <AlertTitle>{testSuccess ? 'Success' : 'Error'}</AlertTitle>
                      <AlertDescription>{testMessage}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={testEmailMutation.isPending || (!settings?.isConfigured && !stats?.provider)}
                    >
                      {testEmailMutation.isPending ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="statistics">
          <Card>
            <CardHeader>
              <CardTitle>Email Statistics</CardTitle>
              <CardDescription>
                Review email delivery statistics and performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.notConfigured ? (
                <Alert variant="warning" className="bg-amber-50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Provider Not Configured</AlertTitle>
                  <AlertDescription>
                    You need to configure an email provider to view statistics.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stats?.total || 0}</div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Delivered</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stats?.delivered || 0}</div>
                        <p className="text-xs text-gray-500">
                          {stats?.total ? Math.round((stats.delivered / stats.total) * 100) : 0}% delivery rate
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Opened</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stats?.opened || 0}</div>
                        <p className="text-xs text-gray-500">
                          {stats?.delivered ? Math.round((stats.opened / stats.delivered) * 100) : 0}% open rate
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Clicked</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stats?.clicked || 0}</div>
                        <p className="text-xs text-gray-500">
                          {stats?.opened ? Math.round((stats.clicked / stats.opened) * 100) : 0}% click rate
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <h3 className="font-medium mb-2">Current Provider</h3>
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 mr-2 text-primary" />
                      <span className="capitalize">{stats?.provider || 'None'}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
